import * as Sentry from '@sentry/react'

/**
 * SENTRY-PAYLOAD-REMEDIATION-1
 *
 * Mechanism notes, read from @sentry/browser + @sentry-internal/replay 10.50.0
 * source (the version in package-lock.json). The browser SDK's options are NOT
 * the same as the Node SDK's:
 *
 *  - There is no `maxIncomingRequestBodySize` in the browser SDK. The browser
 *    never captures an incoming request body, so there is nothing to switch off.
 *  - `requestDataIntegration` is not a browser default. The only thing that
 *    populates `event.request` is `httpContextIntegration.preprocessEvent`,
 *    which sets `{ url, headers: { Referer, 'User-Agent' } }` — no body, no
 *    cookies, no Authorization header. The header deletes below are therefore
 *    defence in depth against future integrations, not a live leak today.
 *  - `sendDefaultPii` in the browser controls only: Relay-side IP inferral
 *    (`_metadata.sdk.settings.infer_ip`), `addAutoIpAddressToSession`, and the
 *    (not-enabled) `httpClientIntegration`. It does not gate bodies.
 *  - The real browser body path is Session Replay. `networkCaptureBodies`
 *    defaults to TRUE; it is inert only because `networkDetailAllowUrls`
 *    defaults to `[]`. Adding a single allow-URL later would start shipping
 *    request and response bodies. Both are pinned explicitly below.
 *  - `beforeSend` runs for error events only; transactions need
 *    `beforeSendTransaction`. XHR/fetch breadcrumbs carry the request body on
 *    the breadcrumb *hint*, which is not serialised into the event and is read
 *    only by Replay's network capture.
 */

/**
 * Matched by case-insensitive SUBSTRING, not exact key, so a header added later
 * that carries a credential is stripped without anyone extending this list.
 */
export const SENSITIVE_HEADER_SUBSTRINGS: readonly string[] = [
  'auth',
  'cookie',
  'token',
  'secret',
  'password',
  'passwd',
  'credential',
  'key',
  'session',
  'signature',
  'assertion',
  'jwt',
  'bearer',
  'otp',
  'pin',
]

/** True if a header name contains any sensitive substring (case-insensitive). */
export function isSensitiveHeaderName(name: string): boolean {
  const lower = name.toLowerCase()
  return SENSITIVE_HEADER_SUBSTRINGS.some((needle) => lower.includes(needle))
}

/** Removes credentials and request payloads from an event of ANY type. */
export function scrubSentryEvent<T extends Sentry.Event>(event: T): T {
  const request = event.request
  if (!request) {
    return event
  }

  delete request.data
  delete request.cookies

  if (request.headers) {
    for (const name of Object.keys(request.headers)) {
      if (isSensitiveHeaderName(name)) {
        delete request.headers[name]
      }
    }
  }

  return event
}

/**
 * SENTRY-CONSOLE-REMEDIATION-1 (frontend)
 *
 * The browser SDK has NO 'Console' integration — `@sentry/browser` 10.50.0
 * `sdk.js` `getDefaultIntegrations()` does not include one, and
 * `@sentry/react` does not export `consoleIntegration`. The console channel
 * in the browser is owned by `breadcrumbsIntegration`, whose options default
 * to `{ console: true, dom: true, fetch: true, history: true, sentry: true,
 * xhr: true }`; its `setup()` calls `addConsoleInstrumentationHandler` when
 * `console` is true. So the backend's "remove the integration" fix has no
 * browser equivalent: removing 'Breadcrumbs' outright would also destroy the
 * click, navigation, fetch and XHR breadcrumbs that make a replay readable.
 *
 * The correct mechanism is to REPLACE the default instance with one that has
 * the console channel off and everything else on.
 *
 * Replay: `@sentry-internal/replay` 10.50.0 `handleBreadcrumbs()` subscribes
 * to the client hook `'beforeAddBreadcrumb'` and routes
 * `category === 'console'` through `normalizeConsoleBreadcrumb()`, which
 * re-serialises `data.arguments` into the replay segment. That hook is emitted
 * by `@sentry/core` `addBreadcrumb()` — i.e. Replay is a CONSUMER of
 * breadcrumbs, not a second console instrumentation. Replay never calls
 * `addConsoleInstrumentationHandler` itself. Starving the source therefore
 * closes the replay path too, and the spec asserts that at the
 * `'beforeAddBreadcrumb'` hook Replay actually subscribes to rather than
 * inferring it.
 */
export const BREADCRUMBS_INTEGRATION_NAME = 'Breadcrumbs'

/**
 * The exact options `Sentry.init` is called with. Exported so tests can drive
 * the real SDK with the shipped configuration rather than a copy of it.
 */
export function buildSentryOptions(params: {
  dsn: string
  environment: string
  release: string
}): Sentry.BrowserOptions {
  const { dsn, environment, release } = params

  return {
    dsn,
    environment,
    release,

    // FUNCTION FORM, deliberately. `getIntegrationsToSetup()` in
    // @sentry/core 10.50.0 `integration.js` does
    // `[...defaultIntegrations, ...userIntegrations]` when this option is an
    // ARRAY, so an array can only ever ADD to the defaults. Only the function
    // form receives the default list and returns the final one.
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter(
        (integration) => integration.name !== BREADCRUMBS_INTEGRATION_NAME,
      ),
      // Same integration, console channel off, every other channel left on.
      Sentry.breadcrumbsIntegration({
        console: false,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true,
      }),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // Pinned explicitly. `networkCaptureBodies` defaults to true and is
        // held off only by an empty allow-list; pinning both means adding an
        // allow-URL later cannot silently start shipping request bodies.
        networkCaptureBodies: false,
        networkDetailAllowUrls: [],
        networkRequestHeaders: [],
        networkResponseHeaders: [],
      }),
    ],

    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    tracePropagationTargets: [
      'localhost',
      /^https:\/\/zephix-backend-staging(-staging)?\.up\.railway\.app/,
      /^https:\/\/zephix-backend-production\.up\.railway\.app/,
      /^https:\/\/api\.getzephix\.com/,
      /^https:\/\/getzephix\.com/,
      /^https:\/\/.*\.zephix\.app/,
    ],

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Stops the user IP being attached to events and sessions, and tells Relay
    // not to infer it server-side.
    sendDefaultPii: false,

    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,

    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  }
}

const dsn = import.meta.env.VITE_SENTRY_DSN

if (dsn) {
  const environment =
    import.meta.env.VITE_ZEPHIX_ENV || import.meta.env.MODE || 'unknown'
  const release = import.meta.env.VITE_SENTRY_RELEASE || 'unknown'

  Sentry.init(buildSentryOptions({ dsn, environment, release }))

  // NOTE: deliberately AFTER init. With the console channel closed this line
  // no longer becomes a breadcrumb inherited by every later event.
  console.log(
    `[Sentry] Initialized - environment: ${environment}, release: ${release}`,
  )
} else {
  console.log('[Sentry] No VITE_SENTRY_DSN configured, monitoring disabled')
}
